/**
 * Coverage-map data source for the Adapter Routing Coverage workspace.
 *
 * CHC-870: switched from the local placeholder to the real backend endpoint
 *   GET /api/internal/payment-adapter/priority/coverage
 * which reports the whole routing surface with computed per-configuration status,
 * counts, amount-coverage, the dimension catalogues and the expected contexts.
 *
 * The mapping below is DEFENSIVE: each field is normalized to the UI model with
 * sane fallbacks, and if the endpoint omits `dimensions` we rebuild them from the
 * same production sources the rest of the admin uses (so the merchant filter and
 * the «Новая конфигурация» dialog keep working):
 *   - merchants            → common getListMerchantsForSelect (role-aware: employee
 *                            list for staff/managers, /api/merchant/list for operators; token + title)
 *   - payment methods      → settings/api/getPaymentMethods
 *   - external-user flags  → constants.CLIENT_FLAG_OPTIONS
 *   - directions           → constants.PAYMENT_DIRRECTION_OPTIONS
 *   - sources              → ['MERCHANT','PLATFORM']
 *   - currencies           → settings/core/_models.Currency enum
 */

import axios from 'axios'
import { API_URL, CLIENT_FLAG_OPTIONS, PAYMENT_DIRRECTION_OPTIONS } from '../../../../constants'
import { getListMerchantsForSelect } from '../../../common/core/_requests'
import type { UserModel } from '../../auth'
import { getPaymentMethods } from './getPaymentMethods'
import { Currency } from '../core/_models'
import {
  statusOfEntry,
  type CoverageResponse,
  type CoverageEntry,
  type CoverageDimensions,
  type ConfigStatus,
} from '../routes/AdapterRoutingCoverage/model/coverage'
import type { RoutingContext } from '../routes/AdapterRoutingCoverage/model/types'

const COVERAGE_URL = `${API_URL}/api/internal/payment-adapter/priority/coverage`

const STATUSES: ConfigStatus[] = ['CONFIGURED', 'EMPTY', 'INCOMPLETE', 'CONFLICT', 'DISABLED']
const DIRECTIONS: ('PAYMENT' | 'PAYOUT')[] = ['PAYMENT', 'PAYOUT']
const SOURCES: ('MERCHANT' | 'PLATFORM')[] = ['MERCHANT', 'PLATFORM']
const FALLBACK_METHODS = ['CARD', 'SBP']

/** Raw shape the backend is expected to return (fields read defensively). */
interface RawCoverage {
  success?: boolean
  dimensions?: Partial<CoverageDimensions>
  configurations?: any[]
  expectedContexts?: any[]
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v.length ? v : fallback
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

function asStatus(v: unknown): ConfigStatus {
  return STATUSES.includes(v as ConfigStatus) ? (v as ConfigStatus) : 'CONFIGURED'
}

function asDirection(v: unknown): 'PAYMENT' | 'PAYOUT' {
  return v === 'PAYOUT' ? 'PAYOUT' : 'PAYMENT'
}

function asSource(v: unknown): 'MERCHANT' | 'PLATFORM' {
  return v === 'PLATFORM' ? 'PLATFORM' : 'MERCHANT'
}

/** Normalize a routing context from the endpoint payload. */
function toContext(raw: any): RoutingContext | null {
  if (!raw || typeof raw !== 'object') return null
  const merchantToken = str(raw.merchantToken)
  const paymentMethodType = str(raw.paymentMethodType)
  if (!merchantToken || !paymentMethodType) return null
  return {
    merchantToken,
    paymentMethodType,
    currency: str(raw.currency, Currency.RUB),
    direction: asDirection(raw.direction),
    externalUserFlag: str(raw.externalUserFlag),
    source: asSource(raw.source),
  }
}

/** Normalize one coverage configuration entry. */
function toEntry(raw: any, index: number): CoverageEntry | null {
  const context = toContext(raw?.context)
  if (!context) return null
  // Per CHC-870/CHC-874 contract the backend reports status as
  // CONFIGURED/INCOMPLETE/CONFLICT and signals «Выключена» via a separate
  // `enabled` flag — the frontend derives the DISABLED display state from it.
  const baseStatus = asStatus(raw?.status)
  const enabled = typeof raw?.enabled === 'boolean' ? raw.enabled : baseStatus !== 'DISABLED'
  const enabledAdapterCount = num(raw?.enabledAdapterCount)
  // CPAYMENT-1485: a context that HAS priority rows but no working adapter is
  // reported INCOMPLETE by the backend (enabledAdapterCount === 0). For the map
  // it is a «Требуют настройки» case (no routing at all), not «Неполные» — fold
  // it into EMPTY so it lands in the needs-setup counter/filter alongside gaps.
  const effectiveStatus: ConfigStatus =
    baseStatus === 'INCOMPLETE' && enabledAdapterCount === 0 ? 'EMPTY' : baseStatus
  const cov = raw?.coverage ?? {}
  return {
    context,
    status: statusOfEntry(effectiveStatus, enabled),
    enabled,
    order: typeof raw?.order === 'number' ? raw.order : index + 1,
    enabledAdapterCount,
    totalAdapterCount: num(raw?.totalAdapterCount),
    coverage: {
      minCovered: typeof cov.minCovered === 'number' ? cov.minCovered : null,
      maxCovered: typeof cov.maxCovered === 'number' ? cov.maxCovered : null,
      hasHoles: Boolean(cov.hasHoles),
      hasOverlaps: Boolean(cov.hasOverlaps),
    },
    updatedAt: str(raw?.updatedAt, new Date().toISOString()),
  }
}

/** Real merchant list (token + title) — fallback when the endpoint omits it. */
async function loadMerchants(currentUser?: UserModel): Promise<Array<{ token: string; title: string }>> {
  try {
    const { data } = await getListMerchantsForSelect(currentUser)
    if (data && data.success && Array.isArray(data.merchants)) {
      return data.merchants
        .filter((m) => m.token)
        .map((m) => ({ token: m.token, title: m.title || m.token }))
    }
  } catch {
    /* empty */
  }
  return []
}

/** Real payment-method types — fallback when the endpoint omits them. */
async function loadPaymentMethods(): Promise<string[]> {
  try {
    const { data } = await getPaymentMethods()
    if (data && data.success && Array.isArray(data.paymentMethods)) {
      const types = data.paymentMethods
        .map((m: any) => m?.type)
        .filter((t: unknown): t is string => typeof t === 'string' && t.length > 0)
      if (types.length) return Array.from(new Set(types))
    }
  } catch {
    /* fall through */
  }
  return FALLBACK_METHODS
}

/**
 * Resolve dimensions: prefer the endpoint payload; otherwise rebuild from the
 * production sources so the merchant filter and the new-config dialog work.
 */
async function resolveDimensions(
  raw?: Partial<CoverageDimensions>,
  currentUser?: UserModel
): Promise<CoverageDimensions> {
  const merchants =
    Array.isArray(raw?.merchants) && raw!.merchants!.length
      ? raw!.merchants!.map((m: any) => ({ token: str(m?.token), title: str(m?.title, str(m?.token)) }))
      : await loadMerchants(currentUser)

  const paymentMethods =
    Array.isArray(raw?.paymentMethods) && raw!.paymentMethods!.length
      ? (raw!.paymentMethods! as any[]).map((t) => str(t)).filter(Boolean)
      : await loadPaymentMethods()

  const currencies =
    Array.isArray(raw?.currencies) && raw!.currencies!.length
      ? (raw!.currencies! as any[]).map((c) => str(c)).filter(Boolean)
      : (Object.values(Currency) as string[])

  const directions =
    Array.isArray(raw?.directions) && raw!.directions!.length
      ? (raw!.directions! as any[]).map(asDirection)
      : ((PAYMENT_DIRRECTION_OPTIONS.map((o) => o.value).filter(
          (v): v is 'PAYMENT' | 'PAYOUT' => v === 'PAYMENT' || v === 'PAYOUT'
        ) as ('PAYMENT' | 'PAYOUT')[]) || DIRECTIONS)

  const externalUserFlags =
    Array.isArray(raw?.externalUserFlags) && raw!.externalUserFlags!.length
      ? (raw!.externalUserFlags! as any[]).map((f) => str(f)).filter(Boolean)
      : CLIENT_FLAG_OPTIONS.map((o) => o.value)

  const sources =
    Array.isArray(raw?.sources) && raw!.sources!.length
      ? (raw!.sources! as any[]).map(asSource)
      : SOURCES

  return {
    merchants,
    paymentMethods,
    currencies,
    directions: directions.length ? directions : DIRECTIONS,
    externalUserFlags,
    sources,
  }
}

/**
 * Fetch the real coverage map for ONE merchant. The backend endpoint is
 * per-merchant (`merchantToken` required) — the page is merchant-first: the
 * operator picks a merchant and we load that merchant's routing surface.
 * Throws on HTTP error so the page shows its error state.
 */
export async function getAdapterRoutingCoverage(
  merchantToken: string,
  currentUser?: UserModel
): Promise<CoverageResponse> {
  const { data } = await axios.get<RawCoverage>(COVERAGE_URL, { params: { merchantToken } })

  const dimensions = await resolveDimensions(data?.dimensions, currentUser)

  const configurations = Array.isArray(data?.configurations)
    ? (data!.configurations!.map(toEntry).filter(Boolean) as CoverageEntry[])
    : []

  const expectedContexts = Array.isArray(data?.expectedContexts)
    ? (data!.expectedContexts!.map(toContext).filter(Boolean) as RoutingContext[])
    : []

  return { dimensions, configurations, expectedContexts }
}
